import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { Location } from '@/services/gql/graphql.generated';
import { FC, Fragment, FunctionComponent, useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import { HiChevronRight } from 'react-icons/hi';
import strings from '../admin.translations';

type DivisionGroup = {
  id: string;
  name: string;
  locations: Location[];
};

type VisibleDivisionGroup = {
  id: string;
  name: string;
  locationCount: number;
  locations: Location[];
};

const columnWidths = {
  id: '16%',
  edit: '16%'
};
const pageSize = 15;

type DivisionTableRowProps = {
  name: string;
  locationCount: number;
  isCollapsed: boolean;
  onToggle: () => void;
};

const DivisionTableRow: FC<DivisionTableRowProps> = ({ name, locationCount, isCollapsed, onToggle }) => {
  return (
    <tr className="cursor-pointer transition-colors" onClick={onToggle}>
      <td colSpan={100} className="px-4 py-2 border-y border-primary-std">
        <div className="flex items-center gap-2">
          <HiChevronRight
            className={[
              'h-8 w-8 text-secondary-std transition-transform duration-150',
              isCollapsed ? '' : 'rotate-90'
            ].join(' ')}
          />
          <div className="flex flex-col items-start">
            <span className="text-xl font-semibold">{name}</span>
            <div className="bg-primary-std text-sm">
              <span>
                {locationCount} Location{locationCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

const LocationTableHeader: FC = () => (
  <thead>
    <tr>
      <th
        className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.id }}
      >
        {strings.locationsSection.table.id}
      </th>
      <th className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std">
        {strings.locationsSection.table.name}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.edit }}
      >
        {strings.locationsSection.table.edit}
      </th>
    </tr>
  </thead>
);

interface LocationTableRowProps {
  location: Location;
  index: number;
  onEdit: (locationId: number) => void;
}

const LocationTableRow: FC<LocationTableRowProps> = ({ location, index, onEdit }) => (
  <tr className={index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std'}>
    <td className="px-4 py-4 text-sm font-medium text-primary-std sm:px-6" style={{ width: columnWidths.id }}>
      <div className="flex h-[2.2rem] items-center">
        <div>{location.id}</div>
      </div>
    </td>
    <td className="px-4 py-4 text-sm font-medium text-primary-std sm:px-6">
      <div className={`flex h-[2.2rem] ${'flex-col justify-center'}`}>
        <div>{location.name}</div>
      </div>
    </td>
    <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.edit }}>
      <div className="flex justify-center">
        <button
          type="button"
          aria-label={`${strings.locationsSection.table.edit} ${location.name}`}
          className="text-secondary-std hover:text-primary-std"
          onClick={() => onEdit(location.id)}
        >
          <FiEdit className="h-5 w-5" />
        </button>
      </div>
    </td>
  </tr>
);

type AdminLocationTableProps = {
  groups: DivisionGroup[];
  onEdit: (locationId: number) => void;
};

const AdminLocationTable: FunctionComponent<AdminLocationTableProps> = ({ groups, onEdit }) => {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const toggleDivision = (id: string) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allLocations = groups.flatMap(g => g.locations);
  const [offset, limit, controls] = usePagination(pageSize, allLocations.length);

  const visibleLocationIds = new Set(allLocations.slice(offset, offset + limit).map(l => l.id));

  const visibleGroups: VisibleDivisionGroup[] = groups
    .map(group => ({
      id: group.id,
      name: group.name,
      locationCount: group.locations.length,
      locations: group.locations.filter(l => visibleLocationIds.has(l.id))
    }))
    .filter(group => group.locations.length > 0);

  return (
    <div className="w-full overflow-hidden">
      <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
        <LocationTableHeader />
        <tbody className="divide-y divide-primary-std">
          {visibleGroups.map(group => {
            const isCollapsed = collapsedIds.has(group.id);
            return (
              <Fragment key={group.id}>
                <DivisionTableRow
                  name={group.name}
                  locationCount={group.locationCount}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleDivision(group.id)}
                />
                {!isCollapsed &&
                  group.locations.map((location, index) => (
                    <LocationTableRow key={location.id} location={location} index={index} onEdit={onEdit} />
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {groups.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};

export default AdminLocationTable;
